<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useIntegrationHook } from 'dashboard/composables/useIntegrationHook';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NemesisEntryInfo from './NemesisEntryInfo.vue';

const props = defineProps({
  integrationId: {
    type: String,
    required: true,
  },
  showAddButton: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['delete', 'add', 'edit']);
const { t, te } = useI18n();

const { integration, isHookTypeInbox, hasConnectedHooks } = useIntegrationHook(
  props.integrationId
);

const globalConfig = useMapGetter('globalConfig/get');
const searchQuery = ref('');
const store = useStore();

// `assigned_agent_id`/`assigned_team_id` are foreign keys (bitrix's settings, see apps.yml) —
// showing the raw numeric id here reads as nonsensical/alarming (e.g. "5" looks like a count of
// something when it's really just agent #5's id) instead of who's actually assigned. Resolved
// against the agents/teams already loaded for the rest of the dashboard.
onMounted(() => {
  store.dispatch('agents/get');
  store.dispatch('teams/get');
});

const agentNameById = id =>
  store.getters['agents/getAgentById'](id)?.name || null;
const teamNameById = id => store.getters['teams/getTeam'](id)?.name || null;

const inboxNameById = id => store.getters['inboxes/getInbox'](id)?.name || null;

const ID_REFERENCE_RESOLVERS = {
  assigned_agent_id: agentNameById,
  assigned_team_id: teamNameById,
  linked_inbox_id: inboxNameById,
};

// Integraciones con `editable_from_list` (apps.yml) se editan desde esta misma pantalla y pueden
// declarar `list_properties`: las columnas del listado, para no mostrar como columna cada campo
// del formulario. Sin eso, se muestran todas las `visible_properties` (como siempre).
const isEditableFromList = computed(
  () => !!integration.value.editable_from_list
);
const listProperties = computed(() =>
  integration.value.list_properties?.length
    ? integration.value.list_properties
    : integration.value.visible_properties || []
);

// El texto de ayuda se busca primero por id de la integración (no cambia si se renombra) y, si
// no existe, por el nombre en mayúsculas como hasta ahora.
const descriptionKey = computed(() => {
  const byId = `INTEGRATION_APPS.SIDEBAR_DESCRIPTION.${integration.value.id.toUpperCase()}`;
  return te(byId)
    ? byId
    : `INTEGRATION_APPS.SIDEBAR_DESCRIPTION.${integration.value.name.toUpperCase()}`;
});

// Most columns are just the raw `visible_properties` key shown as-is (existing behaviour) —
// `hook_name` is the one property every hook-listing screen treats as its display name (see
// apps.yml's bitrix.hook_name and InboxIntegrationsPage.vue's `hookDisambiguator`), so it gets
// a friendly, translated header instead of the raw key.
const PROPERTY_HEADER_LABELS = {
  hook_name: () => t('INTEGRATION_APPS.LIST.HOOK_NAME'),
  connected_at: () => t('INTEGRATION_APPS.LIST.CONNECTED_AT'),
};

// `connected_at` (API Nemesis) la completa el servidor con el primer pedido que llega con el token: en
// el listado se muestra como "Esperando conexión" o "Conectada" más la fecha, no como texto crudo.
const connectionLabel = raw => {
  if (!raw) return t('INTEGRATION_APPS.LIST.WAITING_CONNECTION');
  return t('INTEGRATION_APPS.LIST.CONNECTED_ON', {
    date: new Date(raw).toLocaleString(),
  });
};

const hookHeaders = computed(() => {
  const headers = listProperties.value.map(
    property => PROPERTY_HEADER_LABELS[property]?.() || property
  );
  if (isHookTypeInbox.value) {
    headers.push(t('INTEGRATION_APPS.LIST.INBOX'));
  }
  headers.push(t('INTEGRATION_APPS.LIST.ACTIONS'));
  return headers;
});

// A setting value isn't always a plain string (e.g. bitrix's `survey_buttons` is an array of
// `{ key, label, status_field, status_value }` objects) — showing the full JSON inline blows up
// the row. Instead each cell tracks whether it's collapsed or expanded (see `expandedCells`);
// collapsed shows a small toggle button, expanded shows the complete raw JSON, unsummarized.
const isComplexValue = value => value !== null && typeof value === 'object';

const hooks = computed(() => {
  if (!hasConnectedHooks.value) {
    return [];
  }
  const { hooks: integrationHooks } = integration.value;
  const visibleProperties = listProperties.value;
  return integrationHooks.map(hook => ({
    ...hook,
    id: hook.id,
    properties: visibleProperties.map(property => {
      const raw = hook.settings[property];
      const complex = isComplexValue(raw);
      // `raw` can legitimately be `false` (e.g. bitrix's `status_field_shared`) or `0` — only
      // missing/empty values should render as "--", so this can't be a plain `raw || '--'`
      // (that would print "--" for a deliberately-set `false`, indistinguishable from unset).
      const isBlank = raw === null || raw === undefined || raw === '';
      const resolvedName = !isBlank
        ? ID_REFERENCE_RESOLVERS[property]?.(raw)
        : null;
      if (property === 'connected_at') {
        const label = connectionLabel(raw);
        return {
          key: property,
          raw,
          complex: false,
          display: label,
          searchText: label,
        };
      }
      return {
        key: property,
        raw,
        complex,
        display: complex
          ? JSON.stringify(raw, null, 2)
          : String(isBlank ? '--' : resolvedName || raw),
        searchText: complex
          ? JSON.stringify(raw)
          : String(isBlank ? '' : `${resolvedName || ''} ${raw}`.trim()),
      };
    }),
  }));
});

const filteredHooks = computed(() => {
  const query = searchQuery.value?.trim() || '';
  if (!query) return hooks.value;
  const lowerQuery = query.toLowerCase();
  return (
    hooks.value?.filter(hook =>
      hook.properties?.some(prop =>
        prop.searchText.toLowerCase().includes(lowerQuery)
      )
    ) || []
  );
});

// Keyed by `${hookId}:${property}` so each cell expands independently.
const expandedCells = ref(new Set());
const cellKey = (hookId, property) => `${hookId}:${property}`;
const isExpanded = (hookId, property) =>
  expandedCells.value.has(cellKey(hookId, property));
const toggleExpanded = (hookId, property) => {
  const key = cellKey(hookId, property);
  const next = new Set(expandedCells.value);
  if (next.has(key)) {
    next.delete(key);
  } else {
    next.add(key);
  }
  expandedCells.value = next;
};

const inboxName = hook => (hook.inbox ? hook.inbox.name : '');

// API Nemesis: panel con la URL del webhook y cómo autenticarse (es la misma para todas las
// integraciones de la cuenta: lo que distingue a cada una es su token).
const isNemesis = computed(() => integration.value.id === 'nemesis');
</script>

<template>
  <div class="flex flex-col flex-1 gap-4 overflow-auto">
    <BaseSettingsHeader
      v-model:search-query="searchQuery"
      :title="integration.name || ''"
      :description="
        $t(descriptionKey, { installationName: globalConfig.installationName })
      "
      :feature-name="integrationId"
      :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
      :search-placeholder="$t('INTEGRATION_APPS.SEARCH_PLACEHOLDER')"
    >
      <template v-if="hooks?.length" #count>
        <span class="text-body-main text-n-slate-11">
          {{ $t('INTEGRATION_APPS.COUNT', { n: hooks.length }) }}
        </span>
      </template>
      <template #actions>
        <NextButton
          v-if="showAddButton"
          :label="$t('INTEGRATION_APPS.ADD_BUTTON')"
          size="sm"
          @click="$emit('add')"
        />
      </template>
    </BaseSettingsHeader>
    <div v-if="isNemesis && hasConnectedHooks" class="w-full">
      <NemesisEntryInfo />
    </div>
    <div class="w-full">
      <BaseTable
        v-if="hasConnectedHooks"
        :headers="hookHeaders"
        :items="filteredHooks"
        :no-data-message="searchQuery ? $t('INTEGRATION_APPS.NO_RESULTS') : ''"
      >
        <template #row="{ items }">
          <BaseTableRow v-for="hook in items" :key="hook.id" :item="hook">
            <template #default>
              <BaseTableCell
                v-for="property in hook.properties"
                :key="property.key"
              >
                <div v-if="property.complex" class="flex flex-col gap-1">
                  <NextButton
                    ghost
                    sm
                    slate
                    :icon="
                      isExpanded(hook.id, property.key)
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    :label="
                      isExpanded(hook.id, property.key)
                        ? $t('INTEGRATION_APPS.LIST.HIDE_JSON')
                        : $t('INTEGRATION_APPS.LIST.SHOW_JSON')
                    "
                    @click="toggleExpanded(hook.id, property.key)"
                  />
                  <pre
                    v-if="isExpanded(hook.id, property.key)"
                    class="rounded-lg border border-n-weak bg-n-solid-1 text-xs text-n-slate-12 p-2 overflow-x-auto max-w-md"
                  ><code>{{ property.display }}</code></pre>
                </div>
                <span v-else class="text-body-main text-n-slate-12">
                  {{ property.display }}
                </span>
              </BaseTableCell>

              <BaseTableCell v-if="isHookTypeInbox">
                <span class="text-body-main text-n-slate-11 break-words">
                  {{ inboxName(hook) }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end" class="w-12">
                <div class="flex justify-end gap-3 flex-shrink-0">
                  <NextButton
                    v-if="isEditableFromList"
                    v-tooltip.top="$t('INTEGRATION_APPS.LIST.EDIT.BUTTON_TEXT')"
                    icon="i-lucide-pencil"
                    slate
                    sm
                    @click="$emit('edit', hook)"
                  />
                  <NextButton
                    v-tooltip.top="
                      $t('INTEGRATION_APPS.LIST.DELETE.BUTTON_TEXT')
                    "
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="$emit('delete', hook)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
      <p v-else class="flex flex-col items-center justify-center h-full">
        {{
          $t('INTEGRATION_APPS.NO_HOOK_CONFIGURED', {
            integrationId: integration.id,
          })
        }}
      </p>
    </div>
  </div>
</template>
